import LibraryAPI from "../api/library";
import { libraryContent } from "../activity/content";

const libraryAPI = new LibraryAPI();

/**
 * A library version's link. If details.link is null (restricted), fetches it from the secure
 * endpoint, which checks the member's access. Otherwise, uses the passed-in link directly.
 * Resolves to null when there is no link to play: access was denied (on401/on403 or an alert
 * told the member) or the request failed.
 *
 * @param {Object} options
 * @param {Object} options.details - Video or audio details object from the API response (locales.<lang>)
 * @param {Function} [options.on401] - Callback to trigger when a 401 Unauthorized status is returned
 * @param {Function} [options.on403] - Callback to trigger when a 403 Forbidden status is returned
 */
export const resolveLink = async ({ details, on401, on403 }) => {
    if (details.link || !details.id) return details.link;

    try {
        const response = await libraryAPI.getSecureVideoUrl(details.id);

        if (response && response.status === 200) {
            const data = await response.json();
            if (data.success && data.link) return data.link;

            if (on403) on403();
            else alert(data.message || "An active subscription is required to watch this video.");
        } else if (response && (response.status === 401 || response.status === 403)) {
            if (response.status === 401 && on401) {
                on401();
            } else if (response.status === 403 && on403) {
                on403();
            } else {
                const errorData = await response.json().catch(() => ({}));
                alert(errorData.message || "An active subscription is required to watch this video.");
            }
        } else {
            alert("An error occurred while retrieving the video.");
        }
    } catch (error) {
        console.error("Error fetching video link:", error);
        alert("An error occurred while retrieving the video playback link.");
    }
    return null;
};

/**
 * Resolves video URLs on-demand for restricted videos (see resolveLink), then plays the video.
 * An object payload also gets `activity`: the library version played (details), which the
 * player reports the member's activity for (see src/activity).
 *
 * @param {Object} options
 * @param {Object} options.details - Video details object from the API response
 * @param {Function} options.onSelect - The original onSelect callback function
 * @param {Function} options.buildPayload - A function that takes the resolved link and returns the onSelect payload
 * @param {Function} [options.onError] - Optional callback for handling error state (e.g. closing loaders)
 * @param {Function} [options.on401] - Callback to trigger when a 401 Unauthorized status is returned
 * @param {Function} [options.on403] - Callback to trigger when a 403 Forbidden status is returned
 */
export const resolveAndPlay = async ({ details, onSelect, buildPayload, onError, on401, on403 }) => {
    const link = await resolveLink({ details, on401, on403 });
    if (link === null) {
        if (onError) onError();
        return;
    }

    // Play the video with the resolved link
    const payload = buildPayload(link);
    onSelect(
        payload !== null && typeof payload === "object"
            ? { activity: libraryContent("video", details), ...payload }
            : payload
    );
};

/**
 * An audio version's link as react-jinke-music-player's `musicSrc`: details.link when the API
 * sent it, or else a function the player calls when it loads the track, which resolves the
 * link the way resolveLink does - checking the member's access then, not before - and rejects
 * when there is none to play. The link, once resolved, is kept as the function's `link`.
 *
 * @param {Object} options - As for resolveLink
 */
export const secureMusicSrc = ({ details, on401, on403 }) => {
    if (details.link || !details.id) return details.link || "";

    let pending = null;
    const musicSrc = () => {
        if (musicSrc.link) return Promise.resolve(musicSrc.link);

        // The player asks twice when it loads a new list
        if (!pending) {
            pending = resolveLink({ details, on401, on403 }).then((link) => {
                pending = null;
                if (!link) throw new Error(`No link to play for audio ${details.id}`);
                musicSrc.link = link;
                return link;
            });
        }
        return pending;
    };
    return musicSrc;
};
